/**
 * Which chat automations reference a conversation label — used to warn before a
 * label is deleted: a flow's "Set label" step keeps applying the deleted key, and
 * a skip-list entry keeps excluding chats that still carry it.
 *
 * Standalone on purpose: automation-store pulls in the flow canvas (@xyflow/react),
 * which must not load with the inbox's label picker.
 */
import { collection, getDocsFromServer } from '@/lib/firebase/firestore';
import { db } from '@/config/firebase';
import { useAppStore } from '@/stores/app-store';
import { getEffectiveUid } from '@/stores/auth-store';
import type { ChatAutomationDocument, ChatAutomationScope } from '@/types/automation';

const norm = (s: unknown): string => (typeof s === 'string' ? s.trim().toLowerCase() : '');

/** Names of the flows / auto-reply rules that set or skip `labelKey`. Empty when
 *  none do, or when the automations can't be read — it never blocks a delete. */
export async function findAutomationsUsingLabel(labelKey: string): Promise<string[]> {
  const uid = getEffectiveUid();
  const extensionName = useAppStore.getState().extensionName;
  if (!uid || !extensionName) return [];
  const key = norm(labelKey);
  const skips = (scope?: ChatAutomationScope) => (scope?.skipLabels || []).some((l) => norm(l) === key);
  try {
    // Same collection automation-store reads: one document per phone number.
    const snapshot = await getDocsFromServer(
      collection(db, `ulgebraUsers/${uid}/extensions/${extensionName}/chatAutomations`)
    );
    const names = new Set<string>();
    snapshot.forEach((docSnap) => {
      const data = docSnap.data() as Partial<ChatAutomationDocument>;
      for (const flow of data.flows || []) {
        const setsIt = (flow.nodes || []).some(
          (n) => n.type === 'setConversationLabel' && norm((n.data as { label?: unknown })?.label) === key
        );
        if (setsIt || skips(flow.scope)) names.add(flow.name || 'Untitled flow');
      }
      for (const rule of data.autoReplies || []) {
        if (skips(rule.scope)) names.add(rule.name || 'Untitled rule');
      }
    });
    return [...names];
  } catch (err) {
    console.warn('[Labels] Could not check automations for label usage:', err);
    return [];
  }
}
